import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import { getFlightDetailsWithDedup } from 'api/order'
import { formatAddedBaggage } from 'checkout/lib/utils/flights/format'
import type { ModalContextType } from 'contexts/ModalContext'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getAddedBaggageFromAddons, getLegInclusions } from 'lib/flights/flightUtils'
import { type TransportOption, isLeFlightSavedItemData } from 'tripPlanner/api/types'
import BookedShortlistedFlightDetailsDrawer, { type BookedShortlistedFlightDetailsSection } from './BookedShortlistedFlightDetailsDrawer'
import ShortlistedFlightDetailsDrawer, { type ShortlistedFlightDetailsSection } from './ShortlistedFlightDetailsDrawer'
import type { RefreshedFlight } from './useRefreshShortlistedFlights'
import { getCachedJourney } from './shortlistedJourneyCache'
import type { BookFlightResult } from './useBookShortlistedFlight'

interface UseViewBookedResult {
  viewBookedFlightDetails: (option: TransportOption) => void
  viewUnbookedFlightDetails: (
    option: TransportOption,
    refreshedData?: RefreshedFlight,
    onBookNow?: (option: TransportOption) => void | Promise<BookFlightResult>,
  ) => void
  viewingDetailsItemId: number | undefined
}

interface FlightOrderDetailsTarget {
  orderId?: string
  orderFlightItemId?: string
  preferredLegIndex?: number
}

interface BookedFlightDetailsContext {
  isReturn: boolean
  targets: Array<FlightOrderDetailsTarget>
}

function getBookedFlightDetailsContext(option: TransportOption): BookedFlightDetailsContext {
  const outboundData = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnData = isLeFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined

  const isReturn = Boolean(option.returnLeg && returnData)
  const targets: Array<FlightOrderDetailsTarget> = []
  const seenTargets = new Set<string>()

  const addTarget = (target?: FlightOrderDetailsTarget) => {
    if (!target?.orderId || !target?.orderFlightItemId) {
      return
    }

    const targetKey = `${target.orderId}-${target.orderFlightItemId}-${target.preferredLegIndex ?? 'none'}`
    if (seenTargets.has(targetKey)) {
      return
    }

    seenTargets.add(targetKey)
    targets.push(target)
  }

  addTarget({
    orderId: outboundData?.orderId,
    orderFlightItemId: outboundData?.orderFlightItemId,
    preferredLegIndex: outboundData?.legOrder,
  })
  addTarget({
    orderId: returnData?.orderId,
    orderFlightItemId: returnData?.orderFlightItemId,
    preferredLegIndex: returnData?.legOrder,
  })

  return { isReturn, targets }
}

function getSafeLegIndex(legs: Array<App.Leg>, preferredLegIndex?: number): number | undefined {
  if (legs.length === 0) {
    return undefined
  }

  if (typeof preferredLegIndex === 'number' && legs[preferredLegIndex]) {
    return preferredLegIndex
  }

  return 0
}

function getLegModalIndices({
  legs,
  preferredLegIndex,
  isReturn,
}: {
  legs: Array<App.Leg>
  preferredLegIndex?: number
  isReturn: boolean
}): Array<number> {
  const firstIndex = getSafeLegIndex(legs, preferredLegIndex)

  if (firstIndex == null) {
    return []
  }

  if (isReturn && legs[0] && legs[1]) {
    return [0, 1]
  }

  if (!isReturn || legs.length === 1) {
    return [firstIndex]
  }

  const secondIndex = firstIndex === 0 ? 1 : 0
  if (!legs[secondIndex]) {
    return [firstIndex]
  }

  return [firstIndex, secondIndex]
}

function getHydratedJourneys(option: TransportOption, refreshedData?: RefreshedFlight): {
  isReturn: boolean
  departingJourney?: App.JourneyV2
  returningJourney?: App.JourneyV2
} {
  const savedItemData = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined
  const returnLegData = isLeFlightSavedItemData(option.returnLeg?.savedItemData) ? option.returnLeg.savedItemData : undefined

  const isReturn = Boolean(option.returnLeg && returnLegData)

  const cachedDepartingJourney = savedItemData?.journeyKey
    ? getCachedJourney(savedItemData.journeyKey)?.journey
    : undefined
  const cachedReturningJourney = returnLegData?.journeyKey
    ? getCachedJourney(returnLegData.journeyKey)?.journey
    : undefined

  const departingJourney = refreshedData?.matchedDepartingJourney
    ?? refreshedData?.matchedJourney
    ?? cachedDepartingJourney

  const returningJourney = refreshedData?.matchedReturningJourney
    ?? cachedReturningJourney

  return { isReturn, departingJourney, returningJourney }
}

function useViewBookedShortlistedFlight(showModal: ModalContextType): UseViewBookedResult {
  const intl = useIntl()
  const [viewingDetailsItemId, setViewingDetailsItemId] = useState<number | undefined>(undefined)

  const viewBookedFlightDetails = useCallback(async(option: TransportOption) => {
    const { targets, isReturn } = getBookedFlightDetailsContext(option)

    if (targets.length === 0) {
      showSnackbar(intl.formatMessage({
        id: 'Fylku-gtUXFgXElsmmcQ',
        defaultMessage: 'Flight details are unavailable for this booked flight.',
        description: 'Warning shown when a booked shortlisted flight is missing order identifiers for flight details modal',
      }), 'warning')
      return
    }

    setViewingDetailsItemId(option.itemId)

    try {
      const sections: Array<BookedShortlistedFlightDetailsSection> = []

      for (const [targetIndex, target] of targets.entries()) {
        if (!target.orderId || !target.orderFlightItemId) {
          continue
        }

        const flightDetails = await getFlightDetailsWithDedup({
          orderId: target.orderId,
          itemId: target.orderFlightItemId,
        })

        const legs = flightDetails.bookingJourney?.legs ?? []
        const legIndices = getLegModalIndices({
          legs,
          preferredLegIndex: target.preferredLegIndex,
          isReturn: isReturn && targets.length === 1,
        })

        for (const legIndex of legIndices) {
          const selectedLeg = legs[legIndex]
          if (!selectedLeg) {
            continue
          }

          const legInclusions = getLegInclusions(selectedLeg.inclusions)
          const addedBaggage = getAddedBaggageFromAddons(selectedLeg.addons)
          const addedBaggageFormatted = formatAddedBaggage(addedBaggage)

          sections.push({
            key: `${target.orderId}-${target.orderFlightItemId}-${legIndex}`,
            leg: selectedLeg,
            inclusions: [...legInclusions, ...addedBaggageFormatted],
            reservationInfo: flightDetails.reservationInfo,
            pnrId: flightDetails.pnrId,
            region: flightDetails.region,
            isStandalone: Boolean(flightDetails.metadata?.isStandalone),
            isDeparting: isReturn ? targetIndex === 0 && legIndex === 0 : legIndex === 0,
          })
        }
      }

      if (sections.length === 0) {
        showSnackbar(intl.formatMessage({
          id: 'NWaACR3EZZIYneftyj1S',
          defaultMessage: 'Unable to load flight details right now. Please try again.',
          description: 'Error message when no flight leg data is available after loading booked shortlisted flight details',
        }), 'critical')
        return
      }

      await showModal(<BookedShortlistedFlightDetailsDrawer option={option} sections={sections} />)
    } catch {
      showSnackbar(intl.formatMessage({
        id: 'zIKPyY55c_9sh16J9cyM',
        defaultMessage: 'Unable to load flight details right now. Please try again.',
        description: 'Error snackbar shown when fetching booked shortlisted flight details fails',
      }), 'critical')
    } finally {
      setViewingDetailsItemId(undefined)
    }
  }, [intl, showModal])

  const viewUnbookedFlightDetails = useCallback(async(
    option: TransportOption,
    refreshedData?: RefreshedFlight,
    onBookNow?: (option: TransportOption) => void | Promise<BookFlightResult>,
  ) => {
    const { isReturn, departingJourney, returningJourney } = getHydratedJourneys(option, refreshedData)
    const savedItemData = isLeFlightSavedItemData(option.savedItemData) ? option.savedItemData : undefined

    if (!departingJourney || (isReturn && !returningJourney)) {
      showSnackbar(intl.formatMessage({
        id: '_f4JZXjCfPtWLFHAqf1x',
        defaultMessage: 'Flight details are unavailable for this flight right now.',
        description: 'Warning shown when a non-booked shortlisted flight does not have hydrated journey data for details drawer',
      }), 'warning')
      return
    }

    const displayPrice = refreshedData?.newPrice
      ?? (isReturn
        ? ((departingJourney.price.adult.totalFare ?? 0) + (returningJourney?.price.adult.totalFare ?? 0))
        : departingJourney.price.adult.totalFare)
      ?? (isReturn ? savedItemData?.farePrice : savedItemData?.price)

    const currencyCode = departingJourney.currencyCode
      ?? refreshedData?.matchedDepartingJourney?.currencyCode
      ?? refreshedData?.matchedJourney?.currencyCode
      ?? savedItemData?.fareCurrencyCode
      ?? savedItemData?.currencyCode

    setViewingDetailsItemId(option.itemId)

    try {
      const sections: Array<ShortlistedFlightDetailsSection> = [
        {
          key: `${departingJourney.bookingInfo.journeyKey ?? departingJourney.id}-departing`,
          journey: departingJourney,
        },
      ]

      if (isReturn && returningJourney) {
        sections.push({
          key: `${returningJourney.bookingInfo.journeyKey ?? returningJourney.id}-returning`,
          journey: returningJourney,
        })
      }

      await showModal(<ShortlistedFlightDetailsDrawer
        option={option}
        sections={sections}
        price={displayPrice}
        currencyCode={currencyCode}
        onBookNow={onBookNow}
      />)
    } finally {
      setViewingDetailsItemId(undefined)
    }
  }, [intl, showModal])

  return {
    viewBookedFlightDetails,
    viewUnbookedFlightDetails,
    viewingDetailsItemId,
  }
}

export default useViewBookedShortlistedFlight
